<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCookieConsent } from '../composables/useCookieConsent'
import type { CookieConsent } from '../types/cookie-consent'
import { colors } from '@/design-system'

const { t } = useI18n()
const { isSaving, saveConsent, acceptAll, acceptNecessaryOnly } = useCookieConsent()

const showDetails = ref(false)
const analytics = ref(false)
const marketing = ref(false)
const preferences = ref(false)

const emit = defineEmits<{
  close: []
}>()

async function handleAcceptAll() {
  const success = await acceptAll()
  if (success) {
    emit('close')
  }
}

async function handleRejectAll() {
  const success = await acceptNecessaryOnly()
  if (success) {
    emit('close')
  }
}

async function handleSavePreferences() {
  const consent: CookieConsent = {
    necessary: true,
    analytics: analytics.value,
    marketing: marketing.value,
    preferences: preferences.value,
    timestamp: new Date().toISOString(),
  }
  
  const success = await saveConsent(consent)
  if (success) {
    emit('close')
  }
}
</script>

<template>
  <div class="dialog-overlay" @click.self="emit('close')">
    <div class="dialog-container">
      <div class="dialog-content">
        <!-- Заголовок -->
        <div class="dialog-header">
          <svg class="cookie-icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8z" fill="currentColor"/>
            <circle cx="12" cy="12" r="2" fill="currentColor"/>
            <circle cx="8" cy="10" r="1.5" fill="currentColor"/>
            <circle cx="16" cy="10" r="1.5" fill="currentColor"/>
            <circle cx="9" cy="15" r="1.5" fill="currentColor"/>
            <circle cx="15" cy="15" r="1.5" fill="currentColor"/>
          </svg>
          <h2 class="dialog-title">{{ t('cookie_consent.title') }}</h2>
        </div>

        <!-- Описание -->
        <p class="dialog-description">{{ t('cookie_consent.description') }}</p>

        <!-- Кнопка "Настроить" -->
        <button v-if="!showDetails" class="customize-button" @click="showDetails = true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" stroke-width="2"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" stroke-width="2"/>
          </svg>
          <span>{{ t('cookie_consent.customize') }}</span>
        </button>

        <!-- Детальные настройки -->
        <div v-if="showDetails" class="cookie-categories">
          <div class="divider"></div>

          <!-- Necessary -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3 class="category-title">{{ t('cookie_consent.necessary') }}</h3>
                <p class="category-description">{{ t('cookie_consent.necessary_desc') }}</p>
              </div>
              <label class="switch disabled">
                <input type="checkbox" :checked="true" disabled />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <!-- Analytics -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3 class="category-title">{{ t('cookie_consent.analytics') }}</h3>
                <p class="category-description">{{ t('cookie_consent.analytics_desc') }}</p>
              </div>
              <label class="switch">
                <input v-model="analytics" type="checkbox" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <!-- Marketing -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3 class="category-title">{{ t('cookie_consent.marketing') }}</h3>
                <p class="category-description">{{ t('cookie_consent.marketing_desc') }}</p>
              </div>
              <label class="switch">
                <input v-model="marketing" type="checkbox" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <!-- Preferences -->
          <div class="cookie-category">
            <div class="category-header">
              <div class="category-info">
                <h3 class="category-title">{{ t('cookie_consent.preferences') }}</h3>
                <p class="category-description">{{ t('cookie_consent.preferences_desc') }}</p>
              </div>
              <label class="switch">
                <input v-model="preferences" type="checkbox" />
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="divider"></div>

        <!-- Кнопки действий -->
        <div class="dialog-actions">
          <button 
            class="button button-reject" 
            :disabled="isSaving"
            @click="handleRejectAll"
          >
            {{ t('cookie_consent.reject_all') }}
          </button>
          
          <button 
            v-if="showDetails"
            class="button button-save" 
            :disabled="isSaving"
            @click="handleSavePreferences"
          >
            {{ t('cookie_consent.save_preferences') }}
          </button>
          
          <button 
            class="button button-accept" 
            :disabled="isSaving"
            @click="handleAcceptAll"
          >
            <span v-if="isSaving" class="spinner"></span>
            <span v-else>{{ t('cookie_consent.accept_all') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.dialog-container {
  max-width: 600px;
  max-height: 90vh;
  width: 100%;
  background: white;
  border-radius: 16px;
  overflow: hidden;
}

.dialog-content {
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.cookie-icon {
  color: v-bind('colors.mainBlue');
  flex-shrink: 0;
}

.dialog-title {
  font-size: 24px;
  font-weight: bold;
  color: v-bind('colors.mainBlue');
  margin: 0;
}

.dialog-description {
  font-size: 16px;
  color: #4a5568;
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.customize-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: v-bind('colors.mainBlue');
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background 0.2s;
}

.customize-button:hover {
  background: rgba(64, 76, 118, 0.1);
}

.customize-button svg {
  flex-shrink: 0;
}

.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 24px 0 16px 0;
}

.cookie-categories {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cookie-category {
  background: v-bind('colors.greyBackground');
  border-radius: 8px;
  padding: 16px;
}

.category-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.category-info {
  flex: 1;
}

.category-title {
  font-size: 16px;
  font-weight: 600;
  color: v-bind('colors.mainBlue');
  margin: 0 0 8px 0;
}

.category-description {
  font-size: 14px;
  color: #718096;
  line-height: 1.4;
  margin: 0;
}

/* Toggle Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.3s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: v-bind('colors.mainBlue');
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.switch.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.switch.disabled .slider {
  cursor: not-allowed;
}

/* Кнопки действий */
.dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.button {
  height: 50px;
  padding: 0 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-accept {
  background: v-bind('colors.mainBlue');
  color: white;
}

.button-accept:hover:not(:disabled) {
  background: v-bind('colors.mainBlueLight');
}

.button-save {
  background: v-bind('colors.blueLight');
  color: white;
}

.button-save:hover:not(:disabled) {
  opacity: 0.9;
}

.button-reject {
  background: white;
  color: v-bind('colors.mainBlue');
  border: 2px solid v-bind('colors.mainBlue');
}

.button-reject:hover:not(:disabled) {
  background: rgba(64, 76, 118, 0.05);
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile */
@media (max-width: 600px) {
  .dialog-content {
    padding: 20px;
  }

  .dialog-title {
    font-size: 20px;
  }

  .dialog-description {
    font-size: 14px;
  }

  .dialog-actions {
    flex-direction: column-reverse;
  }

  .button {
    width: 100%;
  }
}
</style>
